import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  NodeResizer,
  Position,
  useEdges,
  useReactFlow,
} from 'reactflow';
import { Box, Typography, IconButton, TextField, Button, CircularProgress } from '@mui/material';
import { Plus, UploadSimple, X, ArrowsOut } from '@phosphor-icons/react';
import { v4 as uuidv4 } from 'uuid';
import { NodeProps } from '../../util/Types/NodeProps';
import useFlowsStore from '../../store/storeFlows';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { useParams } from 'react-router-dom';
import Node from '../UI/Node/Node';
import InputHandle from '../UI/InputHandle/InputHandle';
import { isActiveEdge } from '../../util/findActiveHandle';
import { arrayDataToText } from '../../util/dataToText';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import { normalizeUrls, getMediaType, isValidMediaFile } from './MediaNode/utils/mediaUtils';
import { useMediaUpload } from './MediaNode/hooks/useMediaUpload';
import { CollapseButton } from './MediaNode/components/CollapseButton';
import { UploadService } from '../../service/UploadService';
import { useNotificationStore } from '../../store/storeNotifications';
import { MediaPreview } from './MediaNode/components/MediaPreview';
import { MediaModal } from './MediaNode/components/MediaModal';
import { isUrl } from './MediaNode/utils/mediaUtils';

const DEBOUNCE_DELAY = 400;

const COLLAPSED_WIDTH = 200;
const COLLAPSED_HEIGHT = 60;
const EXPANDED_DEFAULT_WIDTH = 450;
const EXPANDED_MIN_WIDTH = 300;
const EXPANDED_MIN_HEIGHT = 200;
const EXPANDED_MIN_HEIGHT_PX = '150px';
const COLLAPSED_MIN_HEIGHT_PX = '60px';

interface UrlWithId {
  url: string;
  id: string;
  isEdge?: boolean;
  isUploading?: boolean;
  fileName?: string;
}

const createUrlsComparisonString = (urls: UrlWithId[]): string => {
  return urls
    .map(u => `${u.url}|||${u.id}`)
    .sort()
    .join(',');
};

const formatUrlsAsText = (urls: UrlWithId[]): string => {
  return urls
    .map(item => item.url.trim())
    .filter(Boolean)
    .join('\n');
};

const updateNodeSize = (
  nodes: any[],
  nodeId: string,
  width: number,
  height: number | undefined,
  setNodeState: (updater: (nodes: any[]) => any[]) => void,
) => {
  setNodeState(nds =>
    nds.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          width,
          height,
          style: {
            ...node.style,
            width,
            height,
          },
        };
      }
      return node;
    }),
  );
};

function MediaBox({ id, data, type, selected, width, height }: NodeProps) {
  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType?.colors;
  const { setNodes: setNodeState, getNodes, getZoom } = useReactFlow();
  const { setSaving, setSchema, onEdgeDelete } = useFlowsStore(state => state);
  const edges = useEdges();
  const { flowId } = useParams();

  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState<'url' | 'preview'>(
    data?.activeTab || 'url',
  );
  const [modalUrl, setModalUrl] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const [urls, setUrls] = useState<UrlWithId[]>([]);
  const [, setUploadingFiles] = useState<Map<string, string>>(new Map()); 
  const [currentSize, setCurrentSize] = useState<{
    width: number;
    height: number | undefined;
  } | null>(null);

  const isCollapsed = !!data?.collapsed;
  const defaultWidth = isCollapsed ? COLLAPSED_WIDTH : EXPANDED_DEFAULT_WIDTH;
  const defaultHeight = isCollapsed ? COLLAPSED_HEIGHT : undefined;

  const nodeWidth =
    currentSize?.width ??
    (width as number) ??
    (isCollapsed ? COLLAPSED_WIDTH : data?.size?.width) ??
    defaultWidth;
  const nodeHeight =
    currentSize?.height ??
    (height as number) ??
    (isCollapsed ? COLLAPSED_HEIGHT : data?.size?.height) ??
    defaultHeight;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevHasConnectedDataRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  const connectedEdges = useMemo(() => {
    return edges.filter(
      edge =>
        edge.target === id &&
        edge.targetHandle === 'mediaInput' &&
        edge.source &&
        edge.sourceHandle &&
        edge.id,
    );
  }, [edges, id]);

  const hasConnectedData = connectedEdges.length > 0;

  const saveData = useCallback(
    (payload: any, changeType: string = 'default') => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        objectData: payload,
        setSchema,
        changeType,
      });
    },
    [edges, flowId, id, setNodeState, setSaving, setSchema],
  );

  const handleResize = useCallback(
    (event: any, params?: { width?: number; height?: number }) => {
      const nodes = getNodes();
      const currentNode = nodes.find(node => node.id === id);
      if (!currentNode) return;

      const newWidth =
        params?.width ??
        (currentNode.width as number) ??
        EXPANDED_DEFAULT_WIDTH;
      const newHeight =
        params?.height ?? (currentNode.height as number) ?? undefined;

      setCurrentSize({ width: newWidth, height: newHeight });
      updateNodeSize(getNodes(), id, newWidth, newHeight, setNodeState);
    },
    [id, getNodes, setNodeState, saveData],
  );

  const handleResizeEnd = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);

    if (currentNode && currentNode.width && currentNode.height) {
      const finalWidth = currentNode.width as number;
      const finalHeight = currentNode.height as number;

      saveData({ size: { width: finalWidth, height: finalHeight } });
    }

    setCurrentSize(null);
  }, [id, getNodes, saveData]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const nodeElement = document.querySelector(
        `[data-id="${id}"]`,
      ) as HTMLElement;
      if (!nodeElement) return;

      const nodeRect = nodeElement.getBoundingClientRect();

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = nodeWidth;
      const startHeight = nodeHeight || nodeRect.height;

      let animationFrameId: number | null = null;
      let pendingUpdate: { width: number; height: number } | null = null;

      const updateSize = () => {
        if (pendingUpdate) {
          handleResize(null, pendingUpdate);
          pendingUpdate = null;
        }
        animationFrameId = null;
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const currentZoom = getZoom();
        const deltaX = (moveEvent.clientX - startX) / currentZoom;
        const deltaY = (moveEvent.clientY - startY) / currentZoom;

        const newWidth = Math.max(EXPANDED_MIN_WIDTH, startWidth + deltaX);
        const newHeight = Math.max(EXPANDED_MIN_HEIGHT, startHeight + deltaY);

        pendingUpdate = { width: newWidth, height: newHeight };

        if (!animationFrameId) {
          animationFrameId = requestAnimationFrame(updateSize);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }

        if (pendingUpdate) {
          handleResize(null, pendingUpdate);
        }

        handleResizeEnd();
      };

      document.addEventListener('mousemove', handleMouseMove, {
        passive: true,
      });
      document.addEventListener('mouseup', handleMouseUp);
    },
    [id, nodeWidth, nodeHeight, handleResize, handleResizeEnd, getZoom],
  );

  const edgeUrls = useMemo(() => {
    if (!hasConnectedData) return [];

    const urlsFromEdges: UrlWithId[] = [];

    connectedEdges.forEach(edge => {
      const sourceNode = getNodes().find(node => node.id === edge.source);
      if (!sourceNode) return;

      const text =
        arrayDataToText(sourceNode.data?.textData) ||
        sourceNode.data?.text ||
        '';

      if (!text.trim()) return;

      const extractedUrls = normalizeUrls(text);
      extractedUrls.forEach(url => {
        urlsFromEdges.push({
          url,
          id: edge.id,
          isEdge: true,
        });
      });
    });

    return urlsFromEdges;
  }, [hasConnectedData, connectedEdges, getNodes]);

  useEffect(() => {
    if (isInitializedRef.current) return;
    if (hasConnectedData) return;

    const savedUrls = data?.urls || [];

    if (Array.isArray(savedUrls) && savedUrls.length > 0) {
      const parsedUrls: UrlWithId[] = savedUrls
        .filter((item: any) => item && typeof item === 'object' && item.url)
        .map((item: any) => ({
          url: item.url,
          id: item.id || uuidv4(),
          isEdge: item.isEdge || false,
        }));

      if (parsedUrls.length > 0) {
        setUrls(parsedUrls);
      }
    }

    isInitializedRef.current = true;
  }, [data?.urls, hasConnectedData]);

  useEffect(() => {
    const wasConnected = prevHasConnectedDataRef.current;
    const isNowConnected = hasConnectedData && !wasConnected;
    const isNowDisconnected = !hasConnectedData && wasConnected;

    if (isNowConnected) {
      setUrls(edgeUrls);
    }

    if (isNowDisconnected) {
      setUrls([]);
      saveData(
        {
          urls: [] as UrlWithId[],
          text: '',
        },
        'update-edge',
      );
    }

    if (hasConnectedData) {
      setUrls(edgeUrls);
    }

    prevHasConnectedDataRef.current = hasConnectedData;
  }, [hasConnectedData, edgeUrls, saveData]);

  const saveUrlsDebounced = useCallback(
    (urlsToSave: UrlWithId[]) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        saveData({
          urls: urlsToSave,
          text: formatUrlsAsText(urlsToSave),
        });
      }, DEBOUNCE_DELAY);
    },
    [saveData],
  );

  const setNotification = useNotificationStore((state: any) => state.setNotification);

  const uploadFilesWithTracking = useCallback(
    async (files: File[]) => {
      if (!flowId || files.length === 0) return [];

      const uploadingItems: UrlWithId[] = files.map(file => {
        const fileId = uuidv4();
        setUploadingFiles(prev => new Map(prev).set(fileId, file.name));
        return {
          url: file.name,
          id: fileId,
          isUploading: true,
          fileName: file.name,
        };
      });

      setUrls(prev => [...prev, ...uploadingItems]);
      const fileIds = uploadingItems.map(item => item.id);

      try {
        setNotification({
          type: 'info',
          message: 'Uploading files...',
        });

        const uploadPromises = files.map(async file => {
          const formData = new FormData();
          formData.append('file', file);
          const response = await UploadService.uploadFile(formData, flowId);
          return response.data?.src || '';
        });

        const uploadedUrls = await Promise.all(uploadPromises);
        const validUrls = uploadedUrls.filter(Boolean);

        if (validUrls.length > 0) {
          setNotification({
            type: 'success',
            message: 'Files uploaded successfully',
          });

          setUrls(prev => {
            const filtered = prev.filter(u => !fileIds.includes(u.id));
            const urlsWithIds = validUrls.map((url: string, index: number) => ({
              url,
              id: fileIds[index] || uuidv4(),
              isUploading: false,
            }));
            const updatedUrls = [...filtered, ...urlsWithIds];
            saveUrlsDebounced(updatedUrls);
            return updatedUrls;
          });
        }

        setUploadingFiles(prev => {
          const next = new Map(prev);
          fileIds.forEach(id => next.delete(id));
          return next;
        });

        return validUrls;
      } catch (error: any) {
        setNotification({
          type: 'error',
          message: error.message || 'Error uploading files',
        });
        
        setUrls(prev => prev.filter(u => !fileIds.includes(u.id)));
        setUploadingFiles(prev => {
          const next = new Map(prev);
          fileIds.forEach(id => next.delete(id));
          return next;
        });
        return [];
      }
    },
    [flowId, setNotification, saveUrlsDebounced],
  );

  const { handleFileUpload, handleDropFiles } = useMediaUpload({
    flowId,
    hasConnectedData,
    onUrlsChange: useCallback(
      (newUrls: string[]) => {
        const urlsWithIds = newUrls.map(url => ({
          url,
          id: uuidv4(),
        }));
        setUrls(prev => {
          const updatedUrls = [...prev, ...urlsWithIds];
          saveUrlsDebounced(updatedUrls);
          return updatedUrls;
        });
      },
      [saveUrlsDebounced],
    ),
  });

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || hasConnectedData) return;
      
      const files = Array.from(e.target.files);
      const validFiles = files.filter(isValidMediaFile);

      if (validFiles.length === 0) return;

      await uploadFilesWithTracking(validFiles);
    },
    [uploadFilesWithTracking, hasConnectedData],
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (hasConnectedData) return;

      const files = Array.from(e.dataTransfer.files);
      const text = e.dataTransfer.getData('text');
      const textUrls = text ? normalizeUrls(text) : [];

      const allUrls = [...textUrls];

      if (files.length > 0) {
        const validFiles = files.filter(isValidMediaFile);
        if (validFiles.length > 0) {
          const uploadedUrls = await uploadFilesWithTracking(validFiles);
          allUrls.push(...uploadedUrls);
        }
      }

      if (allUrls.length > 0 && textUrls.length > 0) {
        const urlsWithIds = textUrls.map(url => ({
          url,
          id: uuidv4(),
        }));
        setUrls(prev => {
          const updatedUrls = [...prev, ...urlsWithIds];
          saveUrlsDebounced(updatedUrls);
          return updatedUrls;
        });
      }
    },
    [hasConnectedData, uploadFilesWithTracking, saveUrlsDebounced],
  );

  const handleClick = useCallback(() => {
    if (!hasConnectedData && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [hasConnectedData]);

  const handleToggleCollapsed = useCallback(() => {
    saveData({
      collapsed: !data?.collapsed,
    });
  }, [data?.collapsed, saveData]);

  const handleRemoveUrl = useCallback(
    (urlId: string) => {
      if (hasConnectedData) {
        const urlObj = urls.find(u => u.id === urlId);
        if (urlObj && urlObj.isEdge) {
          const edgeId = urlObj.id;

          const edgesToDelete = edges.filter(
            edge =>
              edge.id === edgeId &&
              edge.target === id &&
              edge.targetHandle === 'mediaInput',
          );

          if (edgesToDelete.length > 0 && flowId) {
            if (setNodeState && id) {
              setNodeState((currentNodes: any) => {
                return currentNodes.map((node: any) => {
                  if (node.id === id) {
                    const currentUrls = node.data?.urls || [];
                    const filteredUrls = currentUrls.filter((item: any) => {
                      const itemId = item.id || '';
                      return itemId !== edgeId;
                    });

                    return {
                      ...node,
                      data: {
                        ...node.data,
                        urls: filteredUrls,
                      },
                    };
                  }
                  return node;
                });
              });
            }

            nodeDataAutoSaveDynamic({
              setNodeState,
              id,
              flowId,
              changeType: 'delete-edges',
              setSchema,
              setSaving,
              newEdges: edges,
              deleteEdges: edgesToDelete,
            });

            if (onEdgeDelete) {
              onEdgeDelete(edgesToDelete, edgesToDelete[0]?.id);
            }
          }
        }
      } else {
        const newUrls = urls.filter(u => u.id !== urlId);
        setUrls(newUrls);

        saveData({
          urls: newUrls,
          text: formatUrlsAsText(newUrls),
        });
      }
    },
    [
      hasConnectedData,
      urls,
      edges,
      saveData,
      flowId,
      id,
      setNodeState,
      setSchema,
      setSaving,
      onEdgeDelete,
    ],
  );

  const handleAddUrl = useCallback(() => {
    if (!isUrl(urlInput) || hasConnectedData) return;

    const newUrl: UrlWithId = {
      url: urlInput,
      id: uuidv4(),
    };

    const updatedUrls = [...urls, newUrl];
    setUrls(updatedUrls);
    saveUrlsDebounced(updatedUrls);
    setUrlInput('');
  }, [urlInput, hasConnectedData, urls, saveUrlsDebounced]);

  const checkAndSaveUrls = useCallback(
    (newUrls: UrlWithId[], condition: boolean = true) => {
      if (!condition) return;

      const currentUrls = (data?.urls || []) as UrlWithId[];
      const currentUrlsStr = createUrlsComparisonString(currentUrls);
      const newUrlsStr = createUrlsComparisonString(newUrls);

      if (currentUrlsStr !== newUrlsStr) {
        saveData({
          urls: newUrls,
          text: formatUrlsAsText(newUrls),
        });
      }
    },
    [data?.urls, saveData],
  );

  useEffect(() => {
    if (hasConnectedData || !isInitializedRef.current) return;
    if (urls.length > 0) {
      checkAndSaveUrls(urls);
    }
  }, [urls, hasConnectedData, checkAndSaveUrls]);

  useEffect(() => {
    if (!hasConnectedData) {
      const urlsText = formatUrlsAsText(urls);
      if (data?.text !== urlsText) {
        saveData({ 
          urls: urls,
          text: urlsText 
        });
      }
    }
  }, [urls, hasConnectedData, data?.text, saveData]);

  useEffect(() => {
    if (!hasConnectedData || edgeUrls.length === 0) return;
    checkAndSaveUrls(edgeUrls);
  }, [hasConnectedData, edgeUrls, checkAndSaveUrls]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (data?.activeTab && data.activeTab !== activeTab) {
      setActiveTab(data.activeTab);
    }
  }, [data?.activeTab]);

  useEffect(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    const isCollapsed = !!data?.collapsed;

    if (isCollapsed) {
      if (
        currentNode &&
        (currentNode.width !== COLLAPSED_WIDTH ||
          currentNode.height !== COLLAPSED_HEIGHT)
      ) {
        updateNodeSize(
          getNodes(),
          id,
          COLLAPSED_WIDTH,
          COLLAPSED_HEIGHT,
          setNodeState,
        );
      }
    } else {
      if (currentNode && (!currentNode.width || !currentNode.height)) {
        const savedWidth = data?.size?.width || EXPANDED_DEFAULT_WIDTH;
        const savedHeight = data?.size?.height || undefined;
        updateNodeSize(getNodes(), id, savedWidth, savedHeight, setNodeState);
      } else if (data?.size?.width && data?.size?.height) {
        const savedWidth = data.size.width;
        const savedHeight = data.size.height;

        if (
          currentNode &&
          (currentNode.width !== savedWidth ||
            currentNode.height !== savedHeight)
        ) {
          updateNodeSize(getNodes(), id, savedWidth, savedHeight, setNodeState);
        }
      }
    }
  }, [data?.size, data?.collapsed, id, setNodeState, getNodes]);

  return (
    <>
      <Node
        id={id}
        data={data}
        isLoading={false}
        type={type}
        showTokensUsed={false}
        edges={edges}
      >
        <CollapseButton
          isCollapsed={!!data?.collapsed}
          onToggle={handleToggleCollapsed}
        />

        {selected && !isCollapsed && (
          <NodeResizer
            minHeight={EXPANDED_MIN_HEIGHT}
            minWidth={EXPANDED_MIN_WIDTH}
            onResize={handleResize}
            onResizeEnd={handleResizeEnd}
            isVisible={false}
          />
        )}

        <Box
          sx={{
            position: 'relative',
            overflow: 'visible',
            width: `${nodeWidth - 1}px`,
            height: nodeHeight ? `${nodeHeight}px` : 'auto',
            minHeight: isCollapsed
              ? COLLAPSED_MIN_HEIGHT_PX
              : EXPANDED_MIN_HEIGHT_PX,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              background: '#fff',
              boxShadow: '0 1px 3px rgba(16,24,40,0.08)',
              width: '99.6%', // 0.4% padding to avoid overflow
              height: nodeHeight ? `${nodeHeight}px` : 'auto',
              minHeight: isCollapsed
                ? COLLAPSED_MIN_HEIGHT_PX
                : EXPANDED_MIN_HEIGHT_PX,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {selected && !isCollapsed && (
              <Box
                onMouseDown={handleResizeStart}
                className="nodrag"
                sx={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: findColor?.color || '#3650DE',
                  borderRadius: '4px',
                  cursor: 'nwse-resize',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: findColor?.color || '#3650DE',
                    opacity: 0.9,
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <ArrowsOut
                  size={14}
                  color="#fff"
                  weight="bold"
                  style={{ transform: 'rotate(45deg)' }}
                />
              </Box>
            )}

            {!data?.collapsed && (
              <Box
                p={2.5}
                pb={1}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  overflow: 'hidden',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {(['url', 'preview'] as const).map(tab => (
                  <Box
                    key={tab}
                    component="button"
                    onClick={() => {
                      setActiveTab(tab);
                      saveData({ activeTab: tab });
                    }}
                    style={{
                      border: 'none',
                      background: activeTab === tab ? '#e8ebfc' : '#F2F4F7',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      borderRadius: '8px',
                      color: activeTab === tab ? '#3650DE' : '#667085',
                    }}
                  >
                    {tab === 'url' ? 'URL' : 'Preview'}
                  </Box>
                ))}
              </Box>
            )}

            <Box
              p={2.5}
              pt={1}
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{
                position: 'relative',
                overflow: nodeHeight ? 'auto' : 'visible',
                flex: 1,
                minHeight: 0,
              }}
            >
              {!data?.collapsed && (
                <>
                  {activeTab === 'url' ? (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*,video/*,audio/*,.pdf"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                      />
                      {!hasConnectedData && urls.length === 0 ? (
                        <Box
                          className="nodrag"
                          sx={{
                            border: `2px dashed ${
                              isDragging ? '#667085' : '#D0D5DD'
                            }`,
                            borderRadius: '8px',
                            padding: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onClick={handleClick}
                          onDragOver={e => {
                            e.preventDefault();
                            setIsDragging(true);
                          }}
                          onDragLeave={() => setIsDragging(false)}
                          onDrop={handleDrop}
                          tabIndex={0}
                        >
                          <UploadSimple
                            size={32}
                            weight="regular"
                            color={isDragging ? '#667085' : '#98A2B3'}
                          />
                          <Typography
                            sx={{
                              fontSize: '14px',
                              color: isDragging ? '#667085' : '#98A2B3',
                              textAlign: 'center',
                              fontWeight: 500,
                            }}
                          >
                            Connect or upload visual displays.
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '12px',
                              color: isDragging ? '#667085' : '#98A2B3',
                              textAlign: 'center',
                            }}
                          >
                            PDFs, video URLs, videos, or images.
                          </Typography>
                        </Box>
                      ) : (
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          {urls.map(urlObj => (
                            <Box
                              key={urlObj.id}
                              className="nodrag"
                              sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px',
                                backgroundColor: '#fff',
                                border: '1px solid #667085',
                                borderRadius: '4px',
                                '&:hover .remove-button': {
                                  opacity: urlObj.isUploading ? 0 : 1,
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  flex: 1,
                                  fontSize: '14px',
                                  color: hasConnectedData
                                    ? '#475467'
                                    : '#101828',
                                  wordBreak: 'break-all',
                                  minWidth: 0,
                                  paddingRight: '8px',
                                  userSelect: 'text',
                                  cursor: 'text',
                                }}
                              >
                                {urlObj.url}
                              </Typography>
                              {!urlObj.isUploading && (
                                <IconButton
                                  className="remove-button"
                                  onClick={() => handleRemoveUrl(urlObj.id)}
                                  sx={{
                                    position: 'relative',
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease',
                                    padding: '4px',
                                    backgroundColor: '#fff',
                                    '&:hover': {
                                      backgroundColor: '#fff',
                                    },
                                  }}
                                  title="Remove URL"
                                >
                                  <X size={18} color="#667085" />
                                </IconButton>
                              )}
                              {urlObj.isUploading && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 8,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '12px',
                                      color: '#3650DE',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Uploading
                                  </Typography>
                                  <CircularProgress size={20} sx={{ color: '#3650DE' }} />
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}

                      {!hasConnectedData && (
                        <Box
                          display="flex"
                          gap={1}
                          alignItems="start"
                          className="nodrag"
                        >
                          <TextField
                            label="Add URL"
                            value={urlInput}
                            onChange={e => setUrlInput(e.currentTarget.value)}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            error={!!urlInput && !isUrl(urlInput)}
                            helperText={
                              !!urlInput && !isUrl(urlInput)
                                ? 'Enter a valid URL'
                                : ''
                            }
                            InputLabelProps={{
                              shrink: urlInput.length > 0 || isFocused,
                            }}
                            fullWidth
                          />
                          <Button
                            variant="contained"
                            onClick={handleAddUrl}
                            sx={{
                              background:
                                'linear-gradient(90deg, #BA2F2D 0%, #CA52F1 100%)',
                              color: '#fff',
                              minWidth: '48px',
                              '&.Mui-disabled': {
                                filter: 'grayscale(100%)',
                                opacity: 0.4,
                                color: '#fff',
                              },
                            }}
                            disabled={!isUrl(urlInput)}
                          >
                            <Plus size={18} weight="bold" />
                          </Button>
                          {urls.length > 0 && (
                            <Button
                              variant="contained"
                              onClick={handleClick}
                              sx={{
                                background:
                                  'linear-gradient(90deg, #BA2F2D 0%, #CA52F1 100%)',
                                color: '#fff',
                                minWidth: '48px',
                              }}
                              title="Upload files"
                            >
                              <UploadSimple size={18} weight="bold" />
                            </Button>
                          )}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {urls.length === 0 ? (
                        <Typography sx={{ fontSize: '13px', color: '#667085' }}>
                          {hasConnectedData
                            ? 'No valid URLs found in connected nodes.'
                            : 'No media to display. Add URLs in the URL tab.'}
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            border: '1px solid #EAECF0',
                            borderRadius: '12px',
                            padding: 1.5,
                            background: '#fff',
                            boxShadow: '0 1px 3px rgba(16,24,40,0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          {urls.map(urlObj => {
                            const mediaType = getMediaType(urlObj.url);
                            return (
                              <MediaPreview
                                key={urlObj.id}
                                url={urlObj.url}
                                onClick={
                                  mediaType === 'image' || mediaType === 'pdf'
                                    ? () => setModalUrl(urlObj.url)
                                    : undefined
                                }
                              />
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>

          <InputHandle
            activeColor={findColor?.color}
            handleId="mediaInput"
            isActive={isActiveEdge(edges, id, 'mediaInput', 'target')}
            position={Position.Left}
            type="target"
            top="32px"
            left={-9}
          />

          <InputHandle
            activeColor={findColor?.color}
            handleId="mediaOutput"
            isActive={isActiveEdge(edges, id, 'mediaOutput', 'source')}
            position={Position.Right}
            type="source"
            top="32px"
            right={-8}
          />

          {data?.collapsed && (
            <Typography
              sx={{
                position: 'absolute',
                bottom: '8px',
                left: '12px',
                fontSize: '11px',
                color: '#98A2B3',
                fontWeight: 400,
                pointerEvents: 'none',
              }}
            >
              {urls.length === 0
                ? 'No Media'
                : `${urls.length} ${urls.length === 1 ? 'Media' : 'Medias'}`}
            </Typography>
          )}
        </Box>
      </Node>

      {modalUrl && (
        <MediaModal
          show={!!modalUrl}
          onHide={() => setModalUrl(null)}
          url={modalUrl}
        />
      )}
    </>
  );
}

export default memo(MediaBox);
